import { useState } from "react";
import { useApp } from "../store";
import { INTERACTION_LABELS, type Interaction, type InteractionType, type School } from "../lib/types";

const TYPE_ICON: Record<InteractionType, string> = {
  email_sent: "↗",
  email_received: "↘",
  call: "☎",
  meeting: "◧",
  visit: "⚑",
  form_submitted: "✎",
  questionnaire: "✎",
  other: "•",
};

const blank = (): Partial<Interaction> => ({
  date: new Date().toISOString().slice(0, 10),
  time: null,
  type: "email_sent",
  who: "",
  subject: "",
  body: "",
  followUpDate: null,
});

export function InteractionLog({ school }: { school: School }) {
  const { addInteraction, patchInteraction, deleteInteraction } = useApp();
  const [draft, setDraft] = useState<Partial<Interaction> | null>(null);
  const [editing, setEditing] = useState<string | null>(null);

  async function save() {
    if (!draft) return;
    if (editing) await patchInteraction(school.id, editing, draft);
    else await addInteraction(school.id, draft);
    setDraft(null);
    setEditing(null);
  }

  return (
    <section className="card">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Contact log ({school.interactions.length})</h3>
        {!draft && (
          <button className="btn" onClick={() => setDraft(blank())}>
            + Add entry
          </button>
        )}
      </div>

      {draft && (
        <div className="mt-3 rounded border border-slate-300 bg-slate-50 p-3">
          <div className="flex flex-wrap gap-2">
            <input
              type="date"
              className="input"
              value={draft.date ?? ""}
              onChange={(e) => setDraft({ ...draft, date: e.target.value })}
            />
            <input
              type="time"
              className="input"
              value={draft.time ?? ""}
              onChange={(e) => setDraft({ ...draft, time: e.target.value || null })}
            />
            <select
              className="input"
              value={draft.type}
              onChange={(e) => setDraft({ ...draft, type: e.target.value as InteractionType })}
            >
              {Object.entries(INTERACTION_LABELS).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
            <input
              className="input grow"
              placeholder="Who (e.g. Head Coach Jane Smith)"
              value={draft.who ?? ""}
              onChange={(e) => setDraft({ ...draft, who: e.target.value })}
            />
          </div>
          <input
            className="input mt-2 w-full"
            placeholder="Subject"
            value={draft.subject ?? ""}
            onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
          />
          <textarea
            className="input mt-2 h-28 w-full"
            placeholder="Body — paste the email text or notes from the call"
            value={draft.body ?? ""}
            onChange={(e) => setDraft({ ...draft, body: e.target.value })}
          />
          <div className="mt-2 flex items-center gap-2">
            <label className="text-xs text-slate-500">Follow up by</label>
            <input
              type="date"
              className="input"
              value={draft.followUpDate ?? ""}
              onChange={(e) => setDraft({ ...draft, followUpDate: e.target.value || null })}
            />
            <button className="btn btn-primary ml-auto" onClick={() => void save()}>
              {editing ? "Save changes" : "Add"}
            </button>
            <button
              className="btn"
              onClick={() => {
                setDraft(null);
                setEditing(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <ol className="mt-3 space-y-3">
        {school.interactions.length === 0 && !draft && (
          <li className="text-sm text-slate-400">No contact yet.</li>
        )}
        {school.interactions.map((it) => (
          <li key={it.id} className="border-l-2 border-slate-200 pl-3">
            <div className="flex items-baseline gap-2 text-sm">
              <span className="text-slate-400">{TYPE_ICON[it.type]}</span>
              <span className="font-medium">{INTERACTION_LABELS[it.type]}</span>
              <span className="text-slate-500">
                {it.date}
                {it.time ? ` ${it.time}` : ""}
              </span>
              {it.who && <span className="text-slate-500">· {it.who}</span>}
              <span className="ml-auto flex gap-2">
                <button
                  className="text-xs text-blue-700 hover:underline"
                  onClick={() => {
                    setEditing(it.id);
                    setDraft({ ...it });
                  }}
                >
                  edit
                </button>
                <button
                  className="text-xs text-red-600 hover:underline"
                  onClick={() => void deleteInteraction(school.id, it.id)}
                >
                  delete
                </button>
              </span>
            </div>
            {it.subject && <div className="text-sm font-medium">{it.subject}</div>}
            {it.body && <p className="whitespace-pre-wrap text-sm text-slate-600">{it.body}</p>}
            {it.followUpDate && (
              <div className="mt-1 text-xs text-amber-700">Follow up by {it.followUpDate}</div>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}
